import React from 'react';
import classNames from 'classnames';

import Locale from 'data/locale';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { damageStatList } from 'components/stats/statList';
import { colored } from 'tooltips/builder';
import { formatValue, StatTooltip } from 'components/stats/Stats';
import Tooltip from 'ui/tooltip';

const _L = Locale('ui-calculator');

const damageTypes = {
  damage: 'Damage',
  offhand: 'Off-hand Damage',
  missile: 'Missile Damage',
  trail: 'Trail Damage',
  cloud: 'Cloud Damage',
  explosion: 'Explosion Damage',
  overtime: 'Damage per Second',
  bolt: 'Bolt Damage',
  chain: 'Chain Damage',
  nova: 'Nova Damage',
  burn: 'Burn Damage',
  shard: 'Shard Damage',
  crack: 'Crack Damage',
};

function formatDamage(stat, value, damage) {
  return <>
    {colored(formatValue(stat, value, damage), stat.color(damage))}
    {!!damage.frames && !stat.percent && ' ' + _L`per second`}
    {stat === damageStatList.poison && ' ' + _L`over ${parseFloat((damage.display_poisonlength / 25).toFixed(1))} seconds`}
  </>;
}

function SkillDamageStat({ type, unit, stats, damage }) {
  const stat = damageStatList[type];
  const value = stat.value(damage);
  if (!value) return null;
  return (
    <li>
      <Tooltip tooltip={<StatTooltip stat={stat} stats={damage} unit={unit} allStats={stats}/>} disabled={!stat.tooltip}>
        <span>{_L(stat.name)}: {formatDamage(stat, value, damage)}</span>
      </Tooltip>
    </li>
  );
}

function SkillDamageLine({ type, unit, stats, damage }) {
  const [expanded, setExpanded] = React.useState(false);

  const types = ['phys', 'fire', 'cold', 'light', 'poison', 'magic'].filter(type => damage[`display_${type}mindam`] || damage[`display_${type}maxdam`]);
  const expand = types.length > 1 ? [...types] : [];
  if (damage.display_hitchance) {
    if (!types.length) types.push('phys');
    expand.push('tohit');
  }
  if (damage.display_blockchance) {
    if (!types.length) types.push('phys');
    expand.push('blockchance');
  }

  React.useEffect(() => {
    if (!expand.length) setExpanded(false);
  }, [expand.length]);

  let label = _L(damageTypes[damage.label || type] || 'Damage');
  if (damage.charge) {
    label = _L`Charge ${damage.charge}` + ' - ' + label;
  } else if (damage.sumskill) {
    label = _L`Skill ${damage.sumskill}` + ' - ' + label;
  }

  const stat = types.length === 1 ? damageStatList[types[0]] : damageStatList.total;
  return (
    <div className={classNames("d2p-skill-damage", {"d2p-expanded": expanded, "d2p-expandable": expand.length})}>
      <Tooltip tooltip={<StatTooltip stat={stat} stats={damage} unit={unit} allStats={stats}/>}>
        <span className="d2p-damage-header" onClick={() => expand.length && setExpanded(e => !e)}>
          {label}: {formatDamage(stat, stat.value(damage), damage)}
          {!!expand.length && ' '}
          {!!expand.length && <FontAwesomeIcon icon={expanded ? faCaretUp : faCaretDown}/>}
        </span>
      </Tooltip>
      {expanded && <ul>{expand.map(type => <SkillDamageStat key={type} type={type} unit={unit} stats={stats} damage={damage}/>)}</ul>}
    </div>
  );
}

export default function SkillDamage({ unit, stats, damage }) {
  return Object.entries(damage).map(([type, data]) => !!data && <SkillDamageLine key={type} type={type} unit={unit} stats={stats} damage={data}/>);
}
